import {
	type ApolloClient,
	resetApolloClientSingletons,
} from "@apollo/client-integration-nextjs";

// Every client built in this browser. `ApolloNextAppProvider` owns the client's
// lifetime, so this is the only handle on them — it exists purely so sign-out
// can still clear their stores. Never filled on the server: a server process
// renders every user's requests and never signs out, so a set there would only
// grow.
const clients = new Set<ApolloClient>();

// The key `ApolloNextAppProvider`'s window singleton was built for. In the
// browser the provider builds its client once per window and hands every later
// mount that same client, whatever it is keyed on, so a remount for another
// tenant or host would keep the previous one and its cache.
let singletonKey: string | null = null;

export function trackApolloClient(client: ApolloClient): void {
	if (typeof window !== "undefined") {
		clients.add(client);
	}
}

/**
 * Lets the next `ApolloNextAppProvider` mount build a client for `key` rather
 * than reuse the window singleton built for another tenant or host. Call it
 * during render, before the provider keyed on `key` mounts.
 *
 * The first mount only records its key. The reset also drops the data the
 * server transported for hydration, which belongs to that first mount.
 */
export function releaseApolloClientSingletonFor(key: string): void {
	if (typeof window === "undefined" || singletonKey === key) {
		return;
	}
	if (singletonKey !== null) {
		resetApolloClientSingletons();
	}
	singletonKey = key;
}

// Clears every Apollo client store built in this browser session, so a new
// user signing in on the same browser cannot inherit any cached data from
// prior sessions (including tenants the previous user had visited), and drops
// the window singleton so that user's first mount builds a new client.
// clearStore() can reject with "Store reset while query was in flight" when
// called from an error link handler — Promise.allSettled keeps one
// rejection from blocking the subsequent sign-out.
export async function clearAllApolloClientStores(): Promise<void> {
	await Promise.allSettled(
		Array.from(clients, (client) => client.clearStore()),
	);
	clients.clear();
	if (typeof window !== "undefined") {
		resetApolloClientSingletons();
	}
	singletonKey = null;
}
